import { useMemo } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import COLOR from 'constants/color';
import { Hourglass } from 'assets/icons/revamp';
import TimeWheelPicker from 'components/ui/time-wheel-picker';
import Card from 'components/ui/Card';
import LockedFeaturePreview from 'components/ui/LockedFeaturePreview';
import { DEFAULT_ACTIVITY_CUTOFF } from 'constants/general';
import { t } from 'services/i18n.service';
import {
  updateMoreOptionActivityCutoffTime,
  updateMoreOptionIsLateCutoffEnabled
} from 'store/reducer/modal/slice';
import { moreOptionsActivityCutoffTimeSelector } from 'store/reducer/setting/selectors';
import { addMinutesToTime } from 'utils/time.util';
import {
  getActivityCutoffTimeValidation,
  hasMeaningfulCutoff
} from 'utils/validators';
import { ToggleSwitch } from 'components/common/ToggleSwitch';

const LateCutoff: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    activityCutoffTime,
    isLateCutoffEnabled,
    globalCutoffTime,
    shutdownTime
  } = useAppSelectorShallow(moreOptionsActivityCutoffTimeSelector);

  const { message: validationMessage } = useMemo(
    () => getActivityCutoffTimeValidation(activityCutoffTime),
    [activityCutoffTime]
  );
  const missingCutoffMessage =
    isLateCutoffEnabled && !hasMeaningfulCutoff(activityCutoffTime)
      ? t('validation.invalid_time')
      : null;
  const displayedCutoffMessage = missingCutoffMessage ?? validationMessage;

  const handleToggleLateCutoff = () => {
    if (isLateCutoffEnabled) {
      dispatch(updateMoreOptionIsLateCutoffEnabled(false));
      dispatch(updateMoreOptionActivityCutoffTime(undefined));
      return;
    }
    dispatch(updateMoreOptionIsLateCutoffEnabled(true));

    if (!hasMeaningfulCutoff(activityCutoffTime)) {
      let initialValue = activityCutoffTime;
      if (globalCutoffTime) {
        initialValue = addMinutesToTime(
          globalCutoffTime,
          DEFAULT_ACTIVITY_CUTOFF.OFFSET
        );
      } else if (shutdownTime) {
        initialValue = addMinutesToTime(
          shutdownTime,
          DEFAULT_ACTIVITY_CUTOFF.OFFSET
        );
      } else {
        initialValue = DEFAULT_ACTIVITY_CUTOFF.TIME;
      }
      dispatch(updateMoreOptionActivityCutoffTime(initialValue));
    }
  };

  return (
    <Card className='flex flex-col ~gap-1/2'>
      <div className='flex items-start ~gap-2/3'>
        <Hourglass
          className='~w-4/6 ~h-4/6 mt-0.5'
          fill={COLOR.PRIMARY.LIGHT_600}
          fillDarkMode={COLOR.PRIMARY.DARK_600}
        />
        <div className='flex-1 flex flex-col ~gap-0.5/1'>
          <h3 className='~text-sm/base font-semibold text-black dark:text-white'>
            {t('tabs_revamp.timing_tab.late_cutoff')}
          </h3>
          <p className='~text-xs/sm text-gray-600 dark:text-gray-400'>
            {t('tabs_revamp.timing_tab.late_cutoff_description')}
          </p>
        </div>
        <ToggleSwitch
          checked={isLateCutoffEnabled}
          handleToggle={handleToggleLateCutoff}
          label={t('tabs_revamp.timing_tab.set_late_cutoff')}
          labelStyles='~text-xs/sm text-black dark:text-white'
        />
      </div>

      <LockedFeaturePreview
        isLocked={!isLateCutoffEnabled}
        blurIntensity='sm'
        message={t(
          'tabs_revamp.timing_tab.turn_on_to_skip_this_habit_after_a_specific_time'
        )}>
        <div className='flex items-end ~gap-3/4 flex-wrap ~pt-1/2'>
          <TimeWheelPicker
            key={isLateCutoffEnabled ? 'late-cutoff-set' : 'late-cutoff-unset'}
            initialValue={activityCutoffTime}
            onChange={(time) =>
              isLateCutoffEnabled &&
              dispatch(updateMoreOptionActivityCutoffTime(time))
            }
            size='small'
          />
        </div>
      </LockedFeaturePreview>

      {isLateCutoffEnabled && displayedCutoffMessage ? (
        <p className='~text-[0.7rem]/xs text-red-600 dark:text-red-400'>
          {displayedCutoffMessage}
        </p>
      ) : null}
    </Card>
  );
};

export default LateCutoff;
